const LEVELS = {
	debug: 0,
	info: 1,
	warning: 2,
	error: 3,
	fatal: 4,
};

// LogEntry is a MUTABLE instance of a log message which can be prepared before written
class LogEntry {
	constructor(logger) {
		this._logger = logger;
		this.fields = {};
	}

	// warning: fields are shallow-copied!
	clone() {
		return new LogEntry(this._logger).setFields({ ...this.fields });
	}

	setField(key, val) {
		this.fields[key] = val;

		return this;
	}

	setFields(fields) {
		this.fields = { ...this.fields, ...fields };

		return this;
	}

	setError(e) {
		return this.setField('error', e);
	}

	write(level, message, fields) {
		this._logger.write(level, message, { ...this.fields, ...fields });
	}

	debug(message, fields) {
		this._logger.write('debug', message, { ...this.fields, ...fields });
	}

	info(message, fields) {
		this._logger.write('info', message, { ...this.fields, ...fields });
	}

	warning(message, fields) {
		this._logger.write('warning', message, { ...this.fields, ...fields });
	}

	warn(message, fields) {
		return this.warning(message, fields);
	}

	error(message, fields) {
		this._logger.write('error', message, { ...this.fields, ...fields });
	}

	fatal(message, fields) {
		this._logger.write('fatal', message, { ...this.fields, ...fields });
		process.exit(1); // eslint-disable-line no-process-exit
	}
}

// Logger is a logger instance for the lifetime of an application, which can spawn entries which represent messages
class Logger {
	constructor(level = 'info', outputStream = process.stdout) {
		if (!LEVELS.hasOwnProperty(level))
			throw new Error('log level not supported');

		const hostname = (function getHostname() {
			if (typeof window !== 'undefined')
				return 'unknown';

			// eslint-disable-next-line global-require
			return require('os').hostname();
		}());

		this._minLevel = LEVELS[level];
		this._output = outputStream;
		this._errorHandlers = [];
		this.defaults = {
			_service: 'unknown',
			_hostname: hostname,
			_commit_hash: process.env.GIT_COMMIT || 'dev', // eslint-disable-line camelcase, no-process-env
		};
	}

	setService(svc) {
		this.defaults._service = svc;

		return this;
	}

	addErrorHandler(handlerFn) {
		this._errorHandlers.push(handlerFn);

		return this;
	}

	entry() {
		return new LogEntry(this);
	}

	write(level, message, fields = {}) {
		if (!LEVELS.hasOwnProperty(level))
			return;

		if (this._minLevel > LEVELS[level])
			return;

		let msg = '';

		const error = fields.error;

		const outputFields = {
			_commit_hash: this.defaults._commit_hash, // eslint-disable-line camelcase
			_hostname: this.defaults._hostname,
			_level: level,
			_message: message,
			_service: this.defaults._service,
			_timestamp: new Date().toISOString(),
			...fields,
			error: void 0,
		};

		if (error) {
			outputFields.error = error.message;

			// add extras from potential cher/legacy errors
			if (error.reasons)
				outputFields.error_reasons = error.reasons; // eslint-disable-line camelcase
			if (error.meta)
				outputFields.error_meta = error.meta; // eslint-disable-line camelcase
		}

		try {
			msg = JSON.stringify(outputFields);
		} catch (e) {
			msg = '{"error": "request log could not be serialised"}';
		}

		this._output.write(`${msg}\n`);

		if (error) {
			for (const handler of this._errorHandlers)
				handler(level, error); // not awaited
		}
	}

	debug(message, fields) {
		this.write('debug', message, fields);
	}

	info(message, fields) {
		this.write('info', message, fields);
	}

	warning(message, fields) {
		this.write('warning', message, fields);
	}

	warn(message, fields) {
		return this.warning(message, fields);
	}

	error(message, fields) {
		this.write('error', message, fields);
	}

	fatal(message, fields) {
		this.write('fatal', message, fields);
		process.exit(1); // eslint-disable-line no-process-exit
	}
}

module.exports = Logger;
